import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  ArrowLeft, Phone, Mail, MapPin, StickyNote, BadgeCheck, Wrench,
  FileText, ClipboardList, History, X, Lock,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AddressLink } from '../components/MapsNavProvider';
import { EquipmentCard } from '../components/equipment/EquipmentCard';
import { EquipmentResubmitDialog } from '../components/equipment/EquipmentResubmitDialog';
import { typeLabel, ApprovalBadge, sourceLabel } from '../components/equipment/equipmentMeta';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const get = (path) => axios.get(`${API}${path}`, { withCredentials: true }).then((r) => r.data);

const STATUS_COLORS = {
  paid: 'bg-emerald-900/30 text-emerald-400 border-emerald-700',
  hsp_paid: 'bg-emerald-900/30 text-emerald-400 border-emerald-700',
  pending: 'bg-amber-900/30 text-amber-400 border-amber-700',
  sent: 'bg-blue-900/30 text-blue-300 border-blue-700',
  approved: 'bg-emerald-900/30 text-emerald-400 border-emerald-700',
  declined: 'bg-red-900/30 text-red-400 border-red-700',
  void: 'bg-[#262626] text-[#A3A3A3] border-[#404040]',
};
const statusCls = (s) => STATUS_COLORS[(s || '').toLowerCase()] || 'bg-[#262626] text-[#A3A3A3] border-[#404040]';

const Section = ({ icon: Icon, title, count, children }) => (
  <div className="bg-[#141414] border border-[#262626] rounded-md p-4">
    <h3 className="text-white font-semibold mb-3 flex items-center gap-2 text-sm">
      <Icon className="w-4 h-4 text-[#D4AF37]" /> {title}
      {typeof count === 'number' && <span className="text-[#525252] text-xs font-normal">({count})</span>}
    </h3>
    {children}
  </div>
);

// Read-only line-item table shared by the invoice + estimate viewers.
const DocViewer = ({ doc, kind, onClose }) => {
  if (!doc) return null;
  const items = doc.line_items || [];
  return (
    <Dialog open={!!doc} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="bg-[#141414] border-[#262626] text-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid={`field-${kind}-viewer`}>
        <DialogHeader>
          <DialogTitle className="text-white flex items-center justify-between gap-2 pr-6">
            <span className="flex items-center gap-2">
              {kind === 'invoice' ? <FileText className="w-5 h-5 text-[#D4AF37]" /> : <ClipboardList className="w-5 h-5 text-[#D4AF37]" />}
              {kind === 'invoice' ? 'Invoice' : 'Estimate'} {doc.invoice_number || doc.estimate_number}
            </span>
            <span className="inline-flex items-center gap-1 text-[10px] text-[#525252] font-normal"><Lock className="w-3 h-3" /> Read-only</span>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <span className="text-[#A3A3A3]">{doc.date || '—'}</span>
            <div className="flex items-center gap-2">
              {doc.payer_label && doc.payer_label !== 'Customer' && (
                <Badge className="bg-purple-900/30 text-purple-300 border-purple-700 text-[10px]" data-testid="field-doc-payer-label">{doc.payer_label}</Badge>
              )}
              <Badge className={`text-[10px] ${statusCls(doc.status)}`}>{(doc.status || 'pending').toUpperCase()}</Badge>
            </div>
          </div>
          {doc.scope && (
            <div>
              <p className="text-[#525252] text-[10px] uppercase tracking-wider mb-0.5">Work performed / scope</p>
              <p className="text-white whitespace-pre-wrap">{doc.scope}</p>
            </div>
          )}
          <div>
            <p className="text-[#525252] text-[10px] uppercase tracking-wider mb-1">Line items</p>
            <div className="border border-[#262626] rounded overflow-hidden">
              {items.length === 0 ? <p className="text-[#525252] text-xs p-2">No line items.</p> : items.map((li, i) => (
                <div key={i} className="flex items-start justify-between gap-3 px-3 py-2 border-b border-[#262626] last:border-0">
                  <div className="min-w-0">
                    <p className="text-white">{li.description || li.name || 'Item'}</p>
                    {(li.quantity != null) && <p className="text-[#525252] text-xs">Qty {li.quantity} × {money(li.unit_price)}</p>}
                  </div>
                  <p className="text-white whitespace-nowrap">{money((li.quantity != null ? li.quantity : 1) * (li.unit_price || li.amount || 0))}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="flex items-center justify-between border-t border-[#262626] pt-2">
            <span className="text-[#A3A3A3]">Total</span>
            <span className="text-[#D4AF37] font-bold text-lg">{money(doc.total)}</span>
          </div>
          {kind === 'invoice' && doc.amount_paid != null && (
            <div className="flex items-center justify-between text-xs">
              <span className="text-[#A3A3A3]">Paid</span><span className="text-white">{money(doc.amount_paid)}</span>
            </div>
          )}
          {doc.appointment_id && <p className="text-[#525252] text-[11px]">Related job: {doc.appointment_id}</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const FieldCustomerDetail = ({ customer, onBack }) => {
  const [hub, setHub] = useState(null);
  const [assets, setAssets] = useState(null);       // null = hidden/forbidden
  const [history, setHistory] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [estimates, setEstimates] = useState(null);
  const [viewDoc, setViewDoc] = useState(null);
  const [viewKind, setViewKind] = useState('invoice');
  const [resubmit, setResubmit] = useState(null);

  const cid = customer.id;

  const loadAll = useCallback(async () => {
    get(`/admin/customers/${cid}/hub-summary`).then(setHub).catch(() => setHub(null));
    get(`/field/customers/${cid}/equipment`).then(setAssets).catch(() => setAssets(null));
    get(`/admin/customers/${cid}/service-history`).then((d) => setHistory(d.visits || [])).catch(() => setHistory(null));
    get(`/admin/customers/${cid}/invoices`).then(setInvoices).catch(() => setInvoices(null));
    get(`/admin/customers/${cid}/estimates`).then(setEstimates).catch(() => setEstimates(null));
  }, [cid]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openInvoice = (inv) => { setViewKind('invoice'); setViewDoc(inv); };
  const openEstimate = (est) => { setViewKind('estimate'); setViewDoc(est); };

  // Collect canonical addresses (primary + any extra service addresses already stored).
  // Prefer the hub-summary (canonical customer record) and fall back to the list object.
  const addresses = [];
  const primary = hub?.primary_address || customer.address || customer.service_address;
  if (primary) addresses.push({ label: 'Service', value: primary });
  const extras = hub?.service_addresses || customer.service_addresses || customer.addresses || [];
  extras.forEach((a) => {
    const v = typeof a === 'string' ? a : (a.address || a.full || '');
    if (v && !addresses.find((x) => x.value === v)) addresses.push({ label: a.label || 'Service', value: v });
  });
  const billing = hub?.billing_address || customer.billing_address;
  if (billing && billing !== primary) {
    addresses.push({ label: 'Billing', value: billing });
  }

  return (
    <div data-testid="field-customer-detail" className="space-y-4">
      <button data-testid="field-customer-back" onClick={onBack} className="flex items-center gap-1.5 text-[#A3A3A3] hover:text-white text-sm">
        <ArrowLeft className="w-4 h-4" /> Back to customers
      </button>

      {/* Header + contact */}
      <div className="bg-[#141414] border border-[#262626] rounded-md p-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-['Outfit'] text-2xl font-bold text-white">{customer.name}</h2>
            <div className="flex gap-x-4 gap-y-1 flex-wrap text-[#A3A3A3] text-sm mt-1.5">
              {customer.phone && <a href={`tel:${customer.phone}`} className="flex items-center gap-1.5 hover:text-white" data-testid="field-cust-phone"><Phone className="w-3.5 h-3.5" /> {customer.phone}</a>}
              {customer.email && <a href={`mailto:${customer.email}`} className="flex items-center gap-1.5 hover:text-white" data-testid="field-cust-email"><Mail className="w-3.5 h-3.5" /> {customer.email}</a>}
            </div>
          </div>
          {hub?.membership?.active && (
            <Badge className="bg-[#D4AF37]/15 text-[#D4AF37] border-[#D4AF37]/50" data-testid="field-cust-membership">
              <BadgeCheck className="w-3.5 h-3.5 mr-1" /> {(hub.membership.tier || 'Member').toUpperCase()}
            </Badge>
          )}
        </div>
        {addresses.length > 0 && (
          <div className="mt-3 space-y-1" data-testid="field-cust-addresses">
            {addresses.map((a, i) => (
              <div key={i} className="flex items-start gap-1.5 text-sm">
                <MapPin className="w-3.5 h-3.5 text-[#525252] mt-0.5 shrink-0" />
                <span className="text-[#525252] text-xs uppercase w-14 shrink-0 mt-0.5">{a.label}</span>
                <AddressLink address={a.value} className="text-[#A3A3A3] text-sm" iconClassName="w-3 h-3" testId={`field-addr-${i}`} />
              </div>
            ))}
          </div>
        )}
        {customer.notes && (
          <div className="mt-3 flex items-start gap-1.5 bg-[#0A0A0A] border border-[#262626] rounded px-3 py-2" data-testid="field-cust-notes">
            <StickyNote className="w-3.5 h-3.5 text-[#D4AF37] mt-0.5 shrink-0" />
            <p className="text-[#A3A3A3] text-sm whitespace-pre-wrap">{customer.notes}</p>
          </div>
        )}
      </div>

      {/* Equipment / Assets */}
      {assets !== null && (
        <Section icon={Wrench} title="Equipment & Assets" count={assets.length}>
          {assets.length === 0 ? <p className="text-[#525252] text-sm">No equipment on file.</p> : (
            <div className="space-y-1.5">
              {assets.map((a) => <EquipmentCard key={a.id} a={a} onResubmit={setResubmit} />)}
            </div>
          )}
        </Section>
      )}

      {/* Service history (past jobs) */}
      {history !== null && (
        <Section icon={History} title="Service History" count={history.length}>
          {history.length === 0 ? <p className="text-[#525252] text-sm">No completed jobs yet.</p> : (
            <div className="space-y-2">
              {history.map((v) => (
                <div key={v.completion_id} data-testid={`field-visit-${v.completion_id}`} className="bg-[#0A0A0A] border border-[#262626] rounded px-3 py-2.5">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-white text-sm font-medium">{v.service_type}</p>
                    <span className="text-[#525252] text-xs">{v.date || ''}{v.technician ? ` · ${v.technician}` : ''}</span>
                  </div>
                  {v.work_order_number && <p className="text-[#525252] text-xs">WO #{v.work_order_number}</p>}
                  {v.work_performed && <p className="text-[#A3A3A3] text-sm mt-1 whitespace-pre-wrap">{v.work_performed}</p>}
                  {v.detail?.materials_used && <p className="text-[#737373] text-xs mt-1">Materials: {v.detail.materials_used}</p>}
                  {v.detail?.recommended_followups && <p className="text-amber-300/80 text-xs mt-1">Recommended: {v.detail.recommended_followups}</p>}
                  {(v.installed_equipment || []).length > 0 && (
                    <div className="mt-1.5 space-y-0.5" data-testid={`field-visit-equipment-${v.completion_id}`}>
                      <p className="text-[10px] uppercase tracking-wider text-[#737373]">Installed / serviced equipment</p>
                      {v.installed_equipment.map((e) => (
                        <p key={e.id} className="text-xs text-[#D4D4D4] flex items-center gap-1.5 flex-wrap">
                          {typeLabel(e.type)}{e.brand || e.model ? ` · ${e.brand} ${e.model}` : ''}{e.serial ? ` · S/N ${e.serial}` : ''}
                          <span className="text-[#737373]">{e.installed_by ? `by ${e.installed_by} · ` : ''}{sourceLabel(e.equipment_source)}{e.photo_count ? ` · ${e.photo_count} photo${e.photo_count > 1 ? 's' : ''}` : ''}</span>
                          <ApprovalBadge status={e.approval_status} testid={`field-visit-eq-status-${e.id}`} />
                        </p>
                      ))}
                    </div>
                  )}
                  {(v.invoices || []).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {v.invoices.map((inv) => (
                        <span key={inv.id} className="text-[10px] text-[#A3A3A3] bg-[#141414] border border-[#262626] rounded px-1.5 py-0.5">
                          {inv.kind === 'warranty' ? 'Warranty / HSP ' : ''}{inv.invoice_number || 'Invoice'} · {money(inv.amount)}{inv.amount_paid ? ` · Paid ${money(inv.amount_paid)}` : ''} · {(inv.status || '').toUpperCase()}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </Section>
      )}

      {/* Past invoices */}
      {invoices !== null && (
        <Section icon={FileText} title="Invoices" count={invoices.length}>
          {invoices.length === 0 ? <p className="text-[#525252] text-sm">No invoices.</p> : (
            <div className="space-y-1.5">
              {invoices.map((inv) => (
                <button key={inv.id} data-testid={`field-invoice-${inv.id}`} onClick={() => openInvoice(inv)}
                  className="w-full text-left bg-[#0A0A0A] border border-[#262626] rounded px-3 py-2 flex items-center justify-between gap-2 hover:border-[#D4AF37]/40">
                  <div className="min-w-0">
                    <div className="text-white text-sm flex items-center gap-1.5 flex-wrap">
                      {inv.invoice_number}
                      {inv.payer_label !== 'Customer' && <Badge className="bg-purple-900/30 text-purple-300 border-purple-700 text-[9px]">{inv.payer_label}</Badge>}
                    </div>
                    <p className="text-[#525252] text-xs">{inv.date || ''}</p>
                    {inv.scope && <p className="text-[#737373] text-xs truncate max-w-[220px]" data-testid={`field-invoice-notes-${inv.id}`}>{inv.scope}</p>}
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-white text-sm">{money(inv.total)}</p>
                    {inv.payer_label === 'Customer' && <p className="text-[10px] text-emerald-300" data-testid={`field-invoice-paid-${inv.id}`}>Paid {money(inv.amount_paid)}</p>}
                    <Badge className={`text-[9px] ${statusCls(inv.status)}`}>{(inv.status || 'pending').toUpperCase()}</Badge>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Section>
      )}

      {/* Past estimates */}
      {estimates !== null && (
        <Section icon={ClipboardList} title="Estimates" count={estimates.length}>
          {estimates.length === 0 ? <p className="text-[#525252] text-sm">No estimates.</p> : (
            <div className="space-y-1.5">
              {estimates.map((est) => (
                <button key={est.id} data-testid={`field-estimate-${est.id}`} onClick={() => openEstimate(est)}
                  className="w-full text-left bg-[#0A0A0A] border border-[#262626] rounded px-3 py-2 flex items-center justify-between gap-2 hover:border-[#D4AF37]/40">
                  <div className="min-w-0">
                    <p className="text-white text-sm">{est.estimate_number}</p>
                    <p className="text-[#525252] text-xs">{est.date || ''}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-white text-sm">{money(est.total)}</p>
                    <Badge className={`text-[9px] ${statusCls(est.status)}`}>{(est.status || 'pending').toUpperCase()}</Badge>
                  </div>
                </button>
              ))}
            </div>
          )}
        </Section>
      )}

      <DocViewer doc={viewDoc} kind={viewKind} onClose={() => setViewDoc(null)} />
      {resubmit && <EquipmentResubmitDialog asset={resubmit} onClose={() => setResubmit(null)} onDone={() => { setResubmit(null); loadAll(); }} />}
    </div>
  );
};

export default FieldCustomerDetail;
